/**
 * 크로스플랫폼 파일 다운로드 유틸리티
 *
 * 핵심 원리:
 * - data URI → blob 변환 후 <a download> 로 직접 다운로드 (프록시 불필요)
 * - HTTPS URL (이미지/PDF) → 백엔드 프록시가 Content-Disposition: attachment 붙여서 강제 다운로드
 *   fetch+blob 후 <a download> 가 기본이고, 실패 시에만 숨은 iframe으로 폴백한다
 *   window.open을 쓰지 않으므로 팝업 차단 대상이 아니고 현재 탭도 이탈하지 않는다
 * - HTTPS URL (pptx 등 브라우저가 렌더링 못하는 파일) → window.open으로 자동 다운로드
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const INLINE_VIEWABLE_EXT_RE = /\.(pdf|png|jpe?g|gif|webp)$/i;

/**
 * iOS 사파리가 새 탭에서 바로 보여줄 수 있는 파일(PDF·이미지)인지.
 * HWPX·DOCX·PPTX 같은 문서는 새 탭으로 열면 "다운로드할 수 없음"이 떠서 <a download>로 저장해야 한다(iOS 13+).
 */
export const isInlineViewableBlob = (blob, filename = '') => {
  const type = String(blob?.type || '').toLowerCase();
  if (type.includes('pdf') || type.startsWith('image/')) return true;
  if (type && type !== 'application/octet-stream') return false;
  return INLINE_VIEWABLE_EXT_RE.test(String(filename || ''));
};

/**
 * 인증 API에서 받은 Blob을 기기에 저장한다.
 * iOS 계열은 PDF·이미지만 새 탭으로 열고(미리보기·공유가 자연스럽다), 문서 파일은 <a download>로 저장한다.
 */
export const downloadBlobFile = (blob, filename = 'whatquiz-file') => {
  if (!(blob instanceof Blob)) return false;
  const printableName = Array.from(String(filename || 'whatquiz-file'))
    .map((char) => {
      const code = char.charCodeAt(0);
      return code < 32 || code === 127 ? '_' : char;
    })
    .join('');
  const safeName = printableName
    .replace(/[\\/:*?"<>|]/g, '_')
    .slice(0, 120);
  const blobUrl = URL.createObjectURL(blob);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (isIOS && isInlineViewableBlob(blob, safeName)) {
    const opened = window.open(blobUrl, '_blank');
    if (opened) {
      try {
        opened.opener = null;
      } catch (_) {}
    } else {
      window.location.href = blobUrl;
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return true;
  }

  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = safeName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // iOS 사파리는 "다운로드하겠습니까?" 확인 창을 띄우므로 사용자가 누를 때까지 blob URL 을 살려 둔다
  setTimeout(() => URL.revokeObjectURL(blobUrl), isIOS ? 60_000 : 5_000);
  return true;
};

/**
 * data URI를 Blob으로 변환 후 <a download>로 즉시 다운로드
 */
function downloadDataUri(dataUri, filename) {
  try {
    const [header, base64] = dataUri.split(',');
    const mime = header.match(/:(.*?);/)?.[1] || 'image/png';
    const bytes = atob(base64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const blob = new Blob([arr], { type: mime });
    downloadBlobFile(blob, filename);
  } catch (err) {
    console.error('Data URI 다운로드 실패:', err);
  }
}

/**
 * PPT, 학습지 등 브라우저가 렌더링 못하는 파일
 */
export const downloadAndOpen = (url, filename, options = {}) => {
  if (!url) return;
  const { fallbackToSameTab = true } = options;
  if (url.startsWith('data:')) {
    downloadDataUri(url, filename || 'whatquiz-file');
    return true;
  }
  const newTab = window.open(url, '_blank');
  if (newTab && !newTab.closed) {
    try {
      newTab.opener = null;
    } catch (_) {}
    return true;
  }
  if (fallbackToSameTab) {
    window.location.href = url;
    return;
  }
  return false;
};

function sanitizeProxyFilename(filename, fallback) {
  const printableName = Array.from(String(filename || fallback))
    .map((char) => {
      const code = char.charCodeAt(0);
      return code < 32 || code === 127 ? '_' : char;
    })
    .join('');
  return printableName
    .replace(/[\\/:*?"<>|]/g, '_')
    .slice(0, 120);
}

function startIframeDownload(proxyUrl) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
  iframe.src = proxyUrl;
  document.body.appendChild(iframe);
  setTimeout(() => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  }, 60_000);
}

/**
 * Storage 공개 URL을 백엔드 attachment 프록시로 강제 다운로드한다.
 * fetch+blob 이 기본 경로라 팝업 차단도 없고, 502가 나와도 현재 탭을 이탈하지 않는다.
 * fetch가 실패하면 숨은 iframe으로만 폴백한다.
 */
export const triggerProxyDownload = (url, filename) => {
  const safeName = sanitizeProxyFilename(filename, 'whatquiz-file');
  const params = new URLSearchParams({ url, filename: safeName });
  const proxyUrl = `${API_BASE_URL}/api/gallery/download-proxy?${params}`;

  if (typeof fetch !== 'function') {
    startIframeDownload(proxyUrl);
    return proxyUrl;
  }

  fetch(proxyUrl)
    .then((resp) => {
      if (!resp.ok) throw new Error(`proxy ${resp.status}`);
      return resp.blob();
    })
    .then((blob) => {
      if (!downloadBlobFile(blob, safeName)) {
        startIframeDownload(proxyUrl);
      }
    })
    .catch((err) => {
      console.warn('프록시 다운로드 fallback:', err);
      startIframeDownload(proxyUrl);
    });

  return proxyUrl;
};

/**
 * PDF 등 브라우저가 인라인 뷰어로 열어버리는 파일 → 강제 다운로드
 * - data URI: blob 변환 후 <a download>로 직접 처리
 * - HTTPS URL: 백엔드 프록시 경유 (Content-Disposition: attachment)
 */
export const downloadPdfFile = (url, filename) => {
  if (!url) return;

  const safeName = sanitizeProxyFilename(
    filename,
    `whatquiz-worksheet-${Date.now()}.pdf`,
  );

  if (url.startsWith('data:')) {
    downloadDataUri(url, safeName);
    return;
  }

  return triggerProxyDownload(url, safeName);
};

/**
 * 이미지 등 브라우저가 렌더링하는 파일 → 강제 다운로드
 * - data URI: blob 변환 후 <a download>로 직접 처리 (프록시 불필요, about:blank#blocked 방지)
 * - HTTPS URL: 백엔드 프록시 경유 (Content-Disposition: attachment)
 */
export const downloadImageFile = (url, filename) => {
  if (!url) return;

  const safeName = sanitizeProxyFilename(
    filename,
    `whatquiz-image-${Date.now()}.png`,
  );

  if (url.startsWith('data:')) {
    downloadDataUri(url, safeName);
    return;
  }

  return triggerProxyDownload(url, safeName);
};

/**
 * 다운로드 완료 후 플랫폼별 안내 메시지
 */
export const getDownloadGuideMessage = (filename) => {
  const shortName = filename && filename.length > 25
    ? filename.substring(0, 22) + '...'
    : filename;
  return `${shortName || '파일'}을 만들었어요`;
};

/**
 * 다운로드 Toast 액션
 */
export const getDownloadToastAction = (url) => {
  if (!url) return null;
  return {
    label: '다시 열기',
    onClick: () => window.open(url, '_blank'),
  };
};
